import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import { CalendarPlus, ArrowLeft, Loader2, Info, Search, AlertTriangle } from 'lucide-react';
import { DEMO_TYPES, isMgr, todayStr, demoBase } from './demoConstants';
import SearchSelect from './SearchSelect';
import { Card } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { PageHeader } from './DemoUI';

const Field = ({ label, hint, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

export default function NewDemoPage() {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const base = demoBase(role);
  const navigate = useNavigate();
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });
  const [f, setF] = useState({ trainer_id: '', counsellor_id: '', course_id: '', student_name: '', demo_date: '', demo_time: '', demo_type: '', notes: '',
    lead_number: '', lead_id: '', lead_email: '', lead_phone: '', lead_source: '' });
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('lead_number');
  const [leadNo, setLeadNo] = useState('');
  const [lead, setLead] = useState(null);
  const [choices, setChoices] = useState([]);
  const [looking, setLooking] = useState(false);
  const isTrainer = role === 'trainer';
  const [dups, setDups] = useState([]);
  const [dupAck, setDupAck] = useState(false);

  const checkDuplicates = async (l) => {
    try {
      const r = await api.get('/training/demos/lead-check', {
        params: { lead_number: l.lead_number || '', lead_id: l.lead_id || '', phone: l.phone || '' },
      });
      setDups(r.data.duplicates || []);
      setDupAck(false);
      if (r.data.count) toast.warning(`${r.data.count} existing demo(s) for this lead`);
    } catch { setDups([]); }
  };

  const pickLead = (l) => {
    setLead(l); setChoices([]);
    setF((p) => ({
      ...p,
      student_name: l.name || p.student_name,
      lead_number: l.lead_number, lead_id: l.lead_id,
      lead_email: l.email, lead_phone: l.phone, lead_source: l.lead_source,
      course_id: p.course_id || (masters.courses.find((c) => c.name?.toLowerCase() === (l.course || '').toLowerCase())?.id || ''),
    }));
    toast.success(`Lead ${l.lead_number || ''} — ${l.name}`);
    checkDuplicates(l);
  };

  const fetchLead = async () => {
    const q = leadNo.trim();
    if (!q) return toast.error(mode === 'phone' ? 'Enter the prospect phone number' : 'Enter the CRM lead number');
    setLooking(true); setChoices([]);
    try {
      const params = mode === 'phone' ? { phone: q } : { lead_number: q };
      const r = await api.get('/training/demos/crm-lead', { params });
      if (!r.data.found) { setLead(null); toast.error(r.data.message || 'Lead not found'); return; }
      const list = r.data.leads || (r.data.lead ? [r.data.lead] : []);
      if (list.length > 1) { setLead(null); setChoices(list); toast.info(`${list.length} leads match — pick one`); return; }
      pickLead(list[0]);
    } catch (e) { toast.error(e.response?.data?.detail || 'CRM lookup failed'); }
    finally { setLooking(false); }
  };

  const clearLead = () => {
    setLead(null); setLeadNo(''); setChoices([]); setDups([]); setDupAck(false);
    setF((p) => ({ ...p, lead_number: '', lead_id: '', lead_email: '', lead_phone: '', lead_source: '' }));
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => toast.error('Failed to load masters'));
  }, []);

  const submit = async () => {
    if (!isTrainer && !f.lead_number) return toast.error('Fetch the prospect from Zoho CRM first — it is mandatory');
    if (role !== 'trainer' && !f.trainer_id) return toast.error('Trainer is required');
    if (role !== 'counsellor' && !f.counsellor_id) return toast.error('Counsellor is required');
    if (!f.course_id) return toast.error('Course is required');
    if (!f.demo_date || !f.demo_time || !f.demo_type) return toast.error('Date, time and demo type are required');
    setSaving(true);
    try {
      const r = await api.post('/training/demos', { ...f, confirm_duplicate: dupAck });
      toast.success(`${r.data.demo_code} created`);
      navigate(`${base}/d/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create demo'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="new-demo-page">
      <PageHeader icon={CalendarPlus} eyebrow="Trainer Demo" title="Schedule a New Demo" subtitle="Pre-admission demo — trainer, counsellor and course." testid="new-demo-header">
        <Button variant="outline" size="sm" className="h-10" onClick={() => navigate(base)} data-testid="back-to-queue"><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
      </PageHeader>

      {isTrainer ? (
        <Card className="p-5 border bg-amber-50/70 border-amber-200 flex items-start gap-3" data-testid="trainer-lead-notice">
          <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Info className="w-4 h-4" /></span>
          <div>
            <h2 className="text-[13.5px] font-semibold text-amber-900">Prospect details come from your manager</h2>
            <p className="text-[12px] text-amber-800 mt-0.5">
              The CRM lead lookup is handled by your Training Manager / counsellor. Please <b>get the lead details
              (name, lead number, phone) from your manager</b> and type the prospect name below — your manager
              attaches the CRM lead when they approve this demo.
            </p>
          </div>
        </Card>
      ) : (
      <Card className="p-5 border space-y-3" data-testid="lead-lookup-card">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center shrink-0"><Search className="w-4 h-4" /></span>
          <div className="flex-1">
            <h2 className="text-[13.5px] font-semibold text-slate-900">Pull the prospect from Zoho CRM <span className="text-rose-500">*</span></h2>
            <p className="text-[12px] text-slate-500 mt-0.5">Mandatory — search by <b>Lead Number</b>, or by <b>phone</b> when you don't have the number handy. We fetch the name, email, phone and source.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Select value={mode} onValueChange={(v) => { setMode(v); setLeadNo(''); setChoices([]); }}>
                <SelectTrigger className="h-10 w-40 bg-white text-[13px]" data-testid="lead-search-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="lead_number">Lead Number</SelectItem>
                  <SelectItem value="phone">Phone Number</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={leadNo}
                onChange={(e) => setLeadNo(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fetchLead(); } }}
                placeholder={mode === 'phone' ? 'e.g. 9119081710' : 'e.g. 80692'}
                className="h-10 w-48 bg-white font-mono"
                data-testid="lead-number-input"
              />
              <Button onClick={fetchLead} disabled={looking} className="h-10 bg-violet-600 hover:bg-violet-700" data-testid="lead-fetch-btn">
                {looking ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Fetching…</> : mode === 'phone' ? 'Search by phone' : 'Fetch lead'}
              </Button>
              {(lead || choices.length > 0) && <Button variant="ghost" className="h-10 text-[12px] text-slate-500" onClick={clearLead} data-testid="lead-clear-btn">Clear</Button>}
            </div>
          </div>
        </div>
        {choices.length > 0 && (
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="lead-choice-list">
            <p className="px-3 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">{choices.length} matching leads — pick the right one</p>
            {choices.map((l) => (
              <button key={l.lead_id} type="button" onClick={() => pickLead(l)} className="w-full text-left px-3 py-2.5 hover:bg-violet-50 transition-colors flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`lead-choice-${l.lead_id}`}>
                <span className="text-[13px] font-semibold text-slate-800">{l.name || '—'}</span>
                {l.lead_number ? <span className="text-[11.5px] font-mono text-violet-700">#{l.lead_number}</span> : null}
                {l.phone ? <span className="text-[11.5px] text-slate-600">{l.phone}</span> : null}
                {l.email ? <span className="text-[11.5px] text-slate-500">{l.email}</span> : null}
                {l.lead_status ? <span className="text-[10.5px] uppercase tracking-wider text-slate-400">{l.lead_status}</span> : null}
              </button>
            ))}
          </div>
        )}
        {lead && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex flex-wrap items-center gap-x-5 gap-y-1.5" data-testid="lead-result-card">
            <span className="text-[13px] font-semibold text-emerald-900">{lead.name || '—'}</span>
            <span className="text-[11.5px] font-mono text-emerald-700">#{lead.lead_number}</span>
            {lead.email ? <span className="text-[11.5px] text-emerald-800">{lead.email}</span> : null}
            {lead.phone ? <span className="text-[11.5px] text-emerald-800">{lead.phone}</span> : null}
            {lead.lead_source ? <span className="text-[10.5px] uppercase tracking-wider text-emerald-600">{lead.lead_source}</span> : null}
            {lead.lead_status ? <span className="text-[10.5px] uppercase tracking-wider text-emerald-600">{lead.lead_status}</span> : null}
          </div>
        )}
        {dups.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 space-y-2.5" data-testid="lead-duplicate-warning">
            <p className="text-[12.5px] font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              This lead already has {dups.length} demo{dups.length > 1 ? 's' : ''} — check before you schedule another
            </p>
            <div className="rounded-md border border-amber-200 bg-white divide-y divide-amber-100 overflow-hidden">
              {dups.map((d) => (
                <div key={d.id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]" data-testid={`dup-row-${d.id}`}>
                  <span className="font-mono font-semibold text-slate-800">{d.demo_code}</span>
                  <span className="text-slate-600">{d.demo_date}</span>
                  <span className="text-slate-600">{d.course_name}</span>
                  <span className="text-slate-500">Trainer: {d.trainer_name || '—'}</span>
                  <span className="text-slate-500">Counsellor: {d.counsellor_name || '—'}</span>
                  <span className="uppercase tracking-wider text-[10px] text-slate-400">{(d.status || '').replace('_', ' ')}</span>
                  {d.outcome_result ? <span className="text-[10px] uppercase tracking-wider text-emerald-700">{d.outcome_result.replace('_', ' ')}</span> : null}
                  <span className="text-[10px] text-amber-600">matched on {d.matched_on}</span>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] font-medium text-amber-900 cursor-pointer">
              <Checkbox checked={dupAck} onCheckedChange={(v) => setDupAck(!!v)} data-testid="dup-confirm" />
              Create anyway — I know this lead already had a demo
            </label>
          </div>
        )}
      </Card>
      )}

      <Card className="p-6 border space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {role !== 'trainer' ? (
            <Field label="Trainer" required hint="Type to search the trainer master">
              <SearchSelect value={f.trainer_id} onChange={(v) => set('trainer_id', v)} options={masters.trainers} placeholder="Select trainer" searchPlaceholder="Search trainers…" testid="demo-trainer" className="w-full" />
            </Field>
          ) : (
            <Field label="Trainer"><div className="h-10 flex items-center px-3 rounded-md bg-violet-50 border border-violet-200 text-[13px] font-semibold text-violet-800" data-testid="demo-trainer-self">{user?.full_name || user?.name} (you)</div></Field>
          )}
          {role !== 'counsellor' ? (
            <Field label="Counsellor" required hint="Type to search the counsellor master">
              <SearchSelect value={f.counsellor_id} onChange={(v) => set('counsellor_id', v)} options={masters.counsellors} placeholder="Select counsellor" searchPlaceholder="Search counsellors…" testid="demo-counsellor" className="w-full" />
            </Field>
          ) : (
            <Field label="Counsellor"><div className="h-10 flex items-center px-3 rounded-md bg-indigo-50 border border-indigo-200 text-[13px] font-semibold text-indigo-800" data-testid="demo-counsellor-self">{user?.full_name} (you)</div></Field>
          )}
          <Field label="Course" required hint="Type to search the course master">
            <SearchSelect value={f.course_id} onChange={(v) => set('course_id', v)} options={masters.courses} placeholder="Select course" searchPlaceholder="Search courses…" testid="demo-course" className="w-full" />
          </Field>
          <Field label="Student / Prospect Name" required={!isTrainer} hint={isTrainer ? 'Type the name your manager gave you' : lead ? 'Pulled from CRM — editable' : 'Fetch the CRM lead above to fill this'}>
            <Input value={f.student_name} onChange={(e) => set('student_name', e.target.value)} placeholder="e.g. Rahul Sharma" className="h-10 bg-white" data-testid="demo-student-input" />
          </Field>
          <Field label="Demo Date" required>
            <Input type="date" min={mgr ? undefined : todayStr()} value={f.demo_date} onChange={(e) => set('demo_date', e.target.value)} className="h-10 bg-white" data-testid="demo-date-input" />
          </Field>
          <Field label="Demo Time" required>
            <Input type="time" value={f.demo_time} onChange={(e) => set('demo_time', e.target.value)} className="h-10 bg-white" data-testid="demo-time-input" />
          </Field>
          <Field label="Demo Type" required>
            <Select value={f.demo_type} onValueChange={(v) => set('demo_type', v)}>
              <SelectTrigger className="h-10 bg-white text-[13px]" data-testid="demo-type-input"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent className="bg-white">{DEMO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Notes" hint="Any context for the trainer — prior experience, specific asks, etc.">
          <Textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className="bg-white text-[13px]" data-testid="demo-notes-input" />
        </Field>

        <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-500">
          <Info className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
          <p>{role === 'trainer'
            ? 'Demos you create go to your Training Manager for approval. Past dates are not allowed.'
            : mgr ? 'Demos created by admins/managers are auto-approved and the trainer is notified instantly.'
              : 'Demos you create are auto-approved and the trainer is notified instantly. Past dates are not allowed.'}</p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => navigate(base)} data-testid="cancel-new-demo">Cancel</Button>
          <Button onClick={submit} disabled={saving || (!isTrainer && !f.lead_number) || (dups.length > 0 && !dupAck)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="demo-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Creating…</> : 'Create Demo'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
